import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronRight, Gauge, ImageOff, Package, Plus, Radar, Stamp } from 'lucide-react';
import { Page, StatTile } from '../../components/layout/Page.js';
import { Button } from '../../components/ui/button.js';
import { Badge, Card, CardBody, CardHeader, CardTitle, Code, EmptyState, Skeleton } from '../../components/ui/primitives.js';
import { useCatalogStats, useMoulds } from '../../db/queries.js';
import { useAuth, useCan } from '../../auth/AuthProvider.js';
import { useSyncState } from '../../sync/useSync.js';
import { HEADLINE_GAPS, RULE_BY_ID, countGaps } from '../quality/rules.js';
import { placeOf, useMouldLocations } from '../moulds/useMouldLocations.js';
import { COVERAGE_DAYS, coverageOf } from '../tracking/freshness.js';
import { formatRelative } from '../../lib/format.js';

/**
 * The landing screen.
 *
 * Week 1's job is the catalog, so this answers the catalog questions: how much
 * is in it, how complete is it, and what changed recently. It grows a location
 * board in week 3 rather than pretending to have one now.
 */
export function OverviewPage() {
  const { user } = useAuth();
  const stats = useCatalogStats();
  const recent = useMoulds({});
  const places = useMouldLocations();

  /*
   * Counted from the same rules the Data Quality page lists and the catalog
   * filters by, so the number here and the length of the list it links to are
   * the same statement rather than two that happen to agree today.
   */
  const gapCounts = useMemo(() => (recent ? countGaps(recent) : null), [recent]);

  /*
   * How much of the catalog anybody has actually laid eyes on lately.
   *
   * Every other figure on this page describes the records. This one describes
   * whether they are still true, which is the question none of the others can
   * answer: a catalog nobody scans goes on looking complete and confident right
   * up until somebody walks to a rack and finds it empty.
   */
  const coverage = useMemo(() => {
    if (!places || !recent) return null;
    return coverageOf(recent.map((m) => placeOf(places, m)?.lastScannedAt));
  }, [places, recent]);
  const headlineGaps = useMemo(
    () =>
      HEADLINE_GAPS.map((id) => ({ rule: RULE_BY_ID[id]!, count: gapCounts?.[id] ?? 0 }))
        .filter((g) => g.count > 0)
        .sort((a, b) => b.count - a.count),
    [gapCounts],
  );
  const sync = useSyncState();
  const canCreate = useCan('create', 'moulds');

  const recentlyUpdated = recent
    ? [...recent]
        .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
        .slice(0, 6)
    : undefined;

  return (
    <Page
      title={greeting(user?.name)}
      description={
        sync.bootstrapped
          ? `Catalog last synced ${formatRelative(sync.lastPullAt)}`
          : 'Downloading the catalog to this device…'
      }
      actions={
        canCreate ? (
          <Button variant="primary" asChild>
            <Link to="/moulds/new">
              <Plus className="size-4" /> Add mould
            </Link>
          </Button>
        ) : null
      }
    >
      {!stats ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Moulds" value={stats.total} icon={<Package />} />
            <StatTile
              label="With photo"
              value={stats.withPhoto}
              hint={`${stats.total - stats.withPhoto} without`}
              tone={stats.withPhoto < stats.total ? 'warning' : 'default'}
              icon={<ImageOff />}
            />
            <StatTile label="Projects" value={stats.projects} />
            {/* The one figure that says whether the records are still true.
                Everything else on this page describes what is written down. */}
            {coverage ? (
              <Link to="/tracking?seen=stale" className="contents">
                <StatTile
                  label={`Seen in ${COVERAGE_DAYS} days`}
                  value={`${coverage.seen} / ${coverage.total}`}
                  hint={
                    coverage.percent >= 80
                      ? `${coverage.percent}% of the catalog`
                      : `only ${coverage.percent}% - the rest is a guess`
                  }
                  tone={coverage.percent >= 80 ? 'default' : 'warning'}
                  icon={<Radar />}
                />
              </Link>
            ) : (
              <StatTile label="Companies" value={stats.companies} icon={<Building2 />} />
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* --- ownership split --- */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Moulds by owner</CardTitle>
                <Link to="/masters/companies" className="text-xs font-medium text-primary">
                  Manage companies
                </Link>
              </CardHeader>
              <CardBody>
                {stats.byOwner.length === 0 ? (
                  <EmptyState title="No moulds yet" description="The catalog is empty on this device." />
                ) : (
                  <ul className="space-y-2.5">
                    {stats.byOwner.map((row) => {
                      const pct = stats.total ? Math.round((row.count / stats.total) * 100) : 0;
                      const unassigned = row.id === null;
                      /* Every row is a link now. A breakdown you can only read
                         tells you a number; a breakdown you can click is the
                         way in to the records behind it, which is the only
                         reason anyone looks at it. */
                      const to = unassigned ? '/moulds?gap=owner' : `/moulds?owner=${row.id}`;
                      return (
                        <li key={row.id ?? 'unassigned'}>
                          <Link
                            to={to}
                            className="group block rounded-lg px-1.5 py-1 -mx-1.5 transition-colors hover:bg-surface-sunken focus-visible:bg-surface-sunken"
                            aria-label={`Show the ${row.count} moulds owned by ${row.name}`}
                          >
                            <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                              <span className={unassigned ? 'text-warning' : 'font-medium'}>
                                {row.name}
                                {unassigned ? (
                                  <span className="ml-2 text-xs font-medium text-warning opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                                    Review &amp; assign →
                                  </span>
                                ) : null}
                              </span>
                              <span className="tabular text-muted-foreground">
                                {row.count} · {pct}%
                              </span>
                            </div>
                            {/* A bar, not a pie: comparing lengths against a shared
                                baseline is the one thing the eye does reliably. */}
                            <div className="h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                              <div
                                className={unassigned ? 'h-full bg-warning' : 'h-full bg-primary'}
                                style={{ width: `${Math.max(pct, 2)}%` }}
                              />
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardBody>
            </Card>

            {/* --- next actions --- */}
            <div className="space-y-4">
              {/* Every figure here is a link to the records it counts. A
                  dashboard that only reports a problem leaves someone to go and
                  find it; the point of this card is that the number and the fix
                  are one click apart. */}
              <Card>
                <CardHeader>
                  <CardTitle>Data quality</CardTitle>
                  <Link to="/data-quality" className="text-xs font-medium text-primary">
                    See all
                  </Link>
                </CardHeader>
                <CardBody>
                  {gapCounts === null ? (
                    <Skeleton className="h-24" />
                  ) : headlineGaps.length === 0 ? (
                    <p className="py-2 text-sm text-muted-foreground">
                      Nothing missing. Every mould has an owner, a project and a photo.
                    </p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {headlineGaps.map(({ rule, count }) => (
                        <li key={rule.id}>
                          <Link
                            to={`/moulds?gap=${rule.id}`}
                            className="flex items-center justify-between gap-3 py-2 text-sm transition-colors hover:text-primary"
                          >
                            <span>{rule.short}</span>
                            <span className="flex items-center gap-1.5">
                              <Badge tone={rule.severity === 'blocker' ? 'danger' : 'warning'}>
                                {count}
                              </Badge>
                              <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden />
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Week 1 checklist</CardTitle>
                </CardHeader>
                <CardBody className="space-y-2">
                  <ActionLink
                    to="/data-quality"
                    icon={<Gauge className="size-4" />}
                    title="Review data quality"
                    description="Find gaps before any nameplate is engraved."
                  />
                  <ActionLink
                    to="/moulds"
                    icon={<Package className="size-4" />}
                    title="Verify the catalog"
                    description="Check the imported spec against the shop floor."
                  />
                  <ActionLink
                    to="/masters/companies"
                    icon={<Building2 className="size-4" />}
                    title="Approve master lists"
                    description="Companies, projects, materials and the rest."
                  />
                  <ActionLink
                    to="/masters"
                    icon={<Stamp className="size-4" />}
                    title="Confirm plate geometry"
                    description="90 × 55 mm and the QR window."
                  />
                </CardBody>
              </Card>
            </div>
          </div>

          {/* --- recent --- */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Recently updated</CardTitle>
              <Link to="/moulds" className="text-xs font-medium text-primary">
                View all
              </Link>
            </CardHeader>
            {!recentlyUpdated?.length ? (
              <EmptyState title="Nothing yet" description="Changes you make will appear here." />
            ) : (
              <ul className="divide-y divide-border">
                {recentlyUpdated.map((m) => (
                  <li key={m.id}>
                    <Link
                      to={`/moulds/${encodeURIComponent(m.assetCode)}`}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-sunken"
                    >
                      {m.photoUrl ? (
                        <img src={m.photoUrl} alt="" className="size-8 rounded-md object-cover" />
                      ) : (
                        <div className="grid size-8 place-items-center rounded-md bg-surface-sunken text-subtle-foreground">
                          <ImageOff className="size-3.5" aria-hidden />
                        </div>
                      )}
                      <span className="min-w-0 flex-1">
                        <Code className="font-semibold">{m.assetCode}</Code>
                        <span className="ml-2 text-sm text-muted-foreground">{m.name}</span>
                      </span>
                      <span className="shrink-0 text-xs text-subtle-foreground">
                        {formatRelative(m.updatedAt)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </Page>
  );
}

function ActionLink({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:border-border-strong hover:bg-surface-sunken"
    >
      <span className="mt-0.5 text-primary">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
    </Link>
  );
}

function greeting(name?: string | null): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const first = name?.split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}
